import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkles, PartyPopper, Home, ArrowRight, ChevronDown, Play } from "lucide-react";
import { API } from "../lib/api";

/**
 * RecommendationsSharePage — public, no-auth page at /share/r/:token.
 * Iter AT redesign — reads like a text from your therapist, not a discharge
 * summary. Scannable sections: Today's Session → Home Care → Stretch cards →
 * Next Visit. Only displays THERAPIST-APPROVED content.
 */

function StretchCard({ stretch, index }) {
  const [open, setOpen] = useState(false);
  const hasHowTo = !!(stretch.how_to || "").trim();
  return (
    <div className="bg-paper-50 border border-paper-200 rounded-2xl overflow-hidden" data-testid={`stretch-card-${index}`}>
      <button
        type="button"
        onClick={() => hasHowTo && setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-4 text-left"
        data-testid={`stretch-card-toggle-${index}`}
      >
        <span className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
          <Play className="w-4 h-4" strokeWidth={2} />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-medium text-ink-900 leading-snug">{stretch.name}</span>
          {stretch.dosage && <span className="block text-xs text-ink-500 mt-0.5">{stretch.dosage}</span>}
        </span>
        {hasHowTo && (
          <span className={`w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center text-ink-500 transition-transform ${open ? "rotate-180" : ""}`}>
            <ChevronDown className="w-4 h-4" strokeWidth={2} />
          </span>
        )}
      </button>
      {open && hasHowTo && (
        <div className="px-4 pb-4 pl-[68px] text-sm text-ink-700 leading-relaxed" data-testid={`stretch-card-howto-${index}`}>
          {stretch.how_to}
        </div>
      )}
    </div>
  );
}

function Section({ icon: Icon, iconClass, label, children, testid }) {
  return (
    <section className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-7 mb-4" data-testid={testid}>
      <div className="flex items-center gap-2.5 mb-3">
        <span className={`w-8 h-8 rounded-full flex items-center justify-center ${iconClass}`}>
          <Icon className="w-4 h-4" strokeWidth={2} />
        </span>
        <span className="font-heading text-lg font-medium text-ink-900">{label}</span>
      </div>
      {children}
    </section>
  );
}

export default function RecommendationsSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/portal/r/${token}`);
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.detail || `Could not load (${r.status})`);
        }
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Could not load this page.");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper-50 px-6">
        <div className="max-w-md text-center" data-testid="recs-share-error">
          <div className="label-overline text-terracotta-600 mb-2">Unavailable</div>
          <h1 className="font-heading text-2xl text-ink-900 mb-2">This link can&rsquo;t be opened</h1>
          <p className="text-ink-700">{err}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper-50" data-testid="recs-share-loading">
        <div className="text-ink-500 text-sm">Loading your recommendations…</div>
      </div>
    );
  }

  const c = data.content || {};
  const first = data.client_first_name || "";
  const todays = (c.todays_session || "").trim() || (c.client_message || "").trim();
  const stretchCards = (c.stretches || []).filter((s) => (s?.name || "").trim());
  const legacyStretches = stretchCards.length === 0 ? (c.suggested_stretches || []) : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-paper-50 via-paper-50 to-sage-50/40 px-4 py-10 sm:py-14" data-testid="recs-share-page">
      <div className="max-w-xl mx-auto">
        {/* Hero */}
        <div className="text-center mb-8">
          <span className="w-12 h-12 rounded-full bg-sage-100 text-sage-700 inline-flex items-center justify-center mb-4">
            <Sparkles className="w-5 h-5" strokeWidth={2} />
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl text-ink-900" data-testid="recs-share-hero">
            Great work today{first ? `, ${first}` : ""}!
          </h1>
          <div className="text-ink-500 mt-2 text-sm">
            {data.therapist_name}{data.clinic_name ? ` · ${data.clinic_name}` : ""}
          </div>
        </div>

        {c.therapist_note && (
          <div className="bg-amber-50 border border-amber-200 rounded-3xl p-5 mb-4 text-sm text-ink-900" data-testid="recs-share-note">
            {c.therapist_note}
          </div>
        )}

        {/* Today's session */}
        {todays && (
          <section className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-7 mb-4" data-testid="recs-share-today">
            <p className="text-ink-900 leading-relaxed">{todays}</p>
          </section>
        )}

        {/* Biggest win banner */}
        {(c.biggest_win || "").trim() && (
          <div className="bg-sage-500 text-white rounded-3xl p-5 mb-4 flex items-start gap-3" data-testid="recs-share-win">
            <PartyPopper className="w-5 h-5 shrink-0 mt-0.5" strokeWidth={2} />
            <p className="font-medium leading-relaxed">{c.biggest_win}</p>
          </div>
        )}

        {/* Home care */}
        {!!(c.home_care || []).length && (
          <Section icon={Home} iconClass="bg-sage-100 text-sage-700" label="Home Care" testid="recs-share-home-care">
            <ul className="space-y-2.5">
              {c.home_care.map((item, i) => (
                <li key={i} className="text-ink-900 flex items-start gap-2.5 leading-relaxed">
                  <span className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2.5 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Stretches — instructional cards */}
        {(stretchCards.length > 0 || legacyStretches.length > 0) && (
          <Section icon={Play} iconClass="bg-amber-100 text-amber-700" label="Stretches & Exercises" testid="recs-share-stretches">
            {stretchCards.length > 0 ? (
              <>
                <div className="space-y-2.5">
                  {stretchCards.map((s, i) => <StretchCard key={i} stretch={s} index={i} />)}
                </div>
                <p className="text-xs text-ink-500 mt-3">Tap a stretch for instructions.</p>
              </>
            ) : (
              <ul className="space-y-2.5">
                {legacyStretches.map((item, i) => (
                  <li key={i} className="text-ink-900 flex items-start gap-2.5 leading-relaxed">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}

        {/* When should I come back? */}
        {c.follow_up?.ideal?.when && (
          <Section icon={ArrowRight} iconClass="bg-sage-500 text-white" label="When Should I Come Back?" testid="recs-share-follow-up">
            <p className="text-ink-900 font-medium leading-relaxed">Recommended follow-up: {c.follow_up.ideal.when}</p>
            {c.follow_up.ideal.why && <p className="text-ink-700 leading-relaxed mt-1">{c.follow_up.ideal.why}</p>}
            {c.follow_up.alternative?.when && (
              <p className="text-ink-500 text-sm leading-relaxed mt-2" data-testid="recs-share-follow-up-alt">
                If that timing doesn't work: {c.follow_up.alternative.when}{c.follow_up.alternative.why ? ` — ${c.follow_up.alternative.why}` : ""}
              </p>
            )}
          </Section>
        )}

        {/* Next visit */}
        {(c.next_visit || "").trim() && (
          <Section icon={ArrowRight} iconClass="bg-ink-900 text-white" label="Next Visit" testid="recs-share-next-visit">
            <p className="text-ink-900 leading-relaxed">{c.next_visit}</p>
          </Section>
        )}

        {/* Legacy long-form portal content — only for links approved before the redesign */}
        {!(c.todays_session || "").trim() && (c.progress_portal_content || "").trim() && (
          <section className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-7 mb-4" data-testid="recs-share-portal">
            <div className="label-overline mb-3 text-sage-700">Your progress &amp; what to focus on</div>
            <div className="text-ink-900 whitespace-pre-line leading-relaxed">{c.progress_portal_content}</div>
          </section>
        )}

        <div className="text-center text-xs text-ink-500 mt-10" data-testid="recs-share-footer">
          For educational purposes — not a medical device. Always check with your healthcare provider for ongoing concerns.
        </div>
      </div>
    </div>
  );
}
